import { defineCustomElement, getAttribute, getBooleanAttribute, getReactEventHandler, getTargetByAttribute, isAttrTrue, NectaryElement, updateAttribute, updateBooleanAttribute } from '../utils';
const templateHTML = '<style>:host{display:block}#wrapper{display:flex;flex-direction:column;gap:8px;box-sizing:border-box;width:100%}</style><div id="wrapper"><slot></slot></div>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-radio', class extends NectaryElement {
  #$slot;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$slot = shadowRoot.querySelector('slot');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    const options = {
      signal
    };
    this.role = 'radiogroup';
    this.#$slot.addEventListener('slotchange', this.#onSlotChange, options);
    this.#$slot.addEventListener('keydown', this.#onOptionKeyDown, options);
    this.#$slot.addEventListener('click', this.#onOptionClick, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['value', 'invalid'];
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set invalid(isInvalid) {
    updateBooleanAttribute(this, 'invalid', isInvalid);
  }
  get invalid() {
    return getBooleanAttribute(this, 'invalid');
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'value':
        {
          this.#onValueChange(newVal ?? '');
          break;
        }
      case 'invalid':
        {
          this.#updateInvalid(isAttrTrue(newVal));
          break;
        }
    }
  }
  #onOptionKeyDown = e => {
    switch (e.code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        {
          e.preventDefault();
          const $option = this.#getPrevOption();
          if ($option !== null) {
            $option.focus();
            $option.click();
          }
          break;
        }
      case 'ArrowDown':
      case 'ArrowRight':
        {
          e.preventDefault();
          const $option = this.#getNextOption();
          if ($option !== null) {
            $option.focus();
            $option.click();
          }
          break;
        }
    }
  };
  #onSlotChange = () => {
    this.#onValueChange(this.value);
  };
  #onOptionClick = e => {
    const target = getTargetByAttribute(e, 'value');
    if (target === null || target.hasAttribute('disabled')) {
      return;
    }
    const value = getAttribute(target, 'value');
    this.dispatchEvent(new CustomEvent('-change', {
      detail: value
    }));
  };
  #onValueChange(value) {
    for (const $option of this.#$slot.assignedElements()) {
      const isChecked = value === getAttribute($option, 'value', '');
      updateBooleanAttribute($option, 'checked', isChecked);
    }
  }
  #getFirstOption() {
    for (const $option of this.#$slot.assignedElements()) {
      if ($option.disabled !== true) {
        return $option;
      }
    }
    return null;
  }
  #getLastOption() {
    for (const $option of this.#$slot.assignedElements().reverse()) {
      if ($option.disabled !== true) {
        return $option;
      }
    }
    return null;
  }
  #getNextOption() {
    const $options = this.#getEnabledRadioElements();
    const $selectedOption = this.#findSelectedOption($options);
    const currentIndex = $selectedOption !== null ? $options.indexOf($selectedOption) : -1;
    if (currentIndex < 0) {
      return this.#getFirstOption();
    }
    return $options[(currentIndex + 1) % $options.length];
  }
  #getPrevOption() {
    const $options = this.#getEnabledRadioElements();
    const $selectedOption = this.#findSelectedOption($options);
    const currentIndex = $selectedOption !== null ? $options.indexOf($selectedOption) : -1;
    if (currentIndex < 0) {
      return this.#getLastOption();
    }
    return $options[(currentIndex - 1 + $options.length) % $options.length];
  }
  #getEnabledRadioElements() {
    return this.#$slot.assignedElements().filter(el => !el.hasAttribute('disabled'));
  }
  #findSelectedOption(elements) {
    return elements.find(el => el.checked) ?? null;
  }
  #updateInvalid(isInvalid) {
    for (const opt of this.#$slot.assignedElements()) {
      updateBooleanAttribute(opt, 'data-invalid', isInvalid);
    }
  }
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
});